/**
 * Authenticated Fetch for Shopify Embedded Apps (Frontend-Only)
 * 
 * MODERN APPROACH (2024+):
 * Uses the CDN-hosted App Bridge (app-bridge.js) with the global `shopify` variable.
 * Session tokens are retrieved via `shopify.idToken()` and exchanged for access tokens
 * on the backend via Shopify's Token Exchange API.
 * 
 * This module routes API calls through a Supabase Edge Function proxy that
 * handles CORS and Token Exchange authentication.
 * 
 * @see https://shopify.dev/docs/api/app-home
 * @see https://shopify.dev/docs/apps/build/authentication-authorization/access-tokens/token-exchange
 */

import { setShopDomain as setSupabaseShopDomain } from './supabase';

// Declare the global shopify object type
declare global {
  interface Window {
    shopify?: {
      idToken(): Promise<string>;
      config?: {
        shop?: string;
        apiKey?: string;
        host?: string;
        locale?: string;
      };
      environment?: {
        embedded?: boolean;
        mobile?: boolean;
      };
      toast: {
        show(message: string, options?: { duration?: number; isError?: boolean }): Promise<void>;
      };
      user(): Promise<{
        id: number;
        firstName?: string;
        lastName?: string;
        email?: string;
        accountOwner?: boolean;
      }>;
      support?: {
        registerHandler(handler: null | (() => void | Promise<void>)): Promise<void>;
      };
    };
  }
}

let shopDomain: string | null = null;
let isInitialized = false;

function getRequiredEnv(name: string): string {
  const v = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[name]) as string | undefined;
  if (!v) {
    throw new Error(`[Config] Missing required env var: ${name}`);
  }
  return v;
}

// ---------------------------------------------------------------------------
// Session token caching / throttling
// ---------------------------------------------------------------------------

const ID_TOKEN_TIMEOUT_MS = 10_000;
const ID_TOKEN_FAILURE_COOLDOWN_MS = 30_000;
const SESSION_TOKEN_CACHE_MS = 30_000;

let cachedSessionToken: { token: string; expiresAt: number } | null = null;
let inFlightSessionToken: Promise<string | null> | null = null;
let idTokenCooldownUntil = 0;

// ---------------------------------------------------------------------------
// Proxy request limiting / retry
// ---------------------------------------------------------------------------

const PROXY_MAX_CONCURRENCY =
  (typeof import.meta !== 'undefined' && Number(import.meta.env?.VITE_SHOPIFY_PROXY_MAX_CONCURRENCY))
  || 4;

const PROXY_MAX_RETRIES =
  (typeof import.meta !== 'undefined' && Number(import.meta.env?.VITE_SHOPIFY_PROXY_MAX_RETRIES))
  || 3;

let activeProxyRequests = 0;
const proxyWaiters: Array<() => void> = [];

async function acquireProxySlot(): Promise<void> {
  if (activeProxyRequests < PROXY_MAX_CONCURRENCY) {
    activeProxyRequests += 1;
    return;
  }
  await new Promise<void>((resolve) => proxyWaiters.push(resolve));
  activeProxyRequests += 1;
}

function releaseProxySlot(): void {
  activeProxyRequests = Math.max(0, activeProxyRequests - 1);
  const next = proxyWaiters.shift();
  if (next) next();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class ProxyHttpError extends Error {
  status: number;
  bodyText: string;

  constructor(status: number, bodyText: string) {
    super(`Shopify API proxy error: ${status} - ${bodyText}`);
    this.name = 'ProxyHttpError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

class ProxyGraphQLError extends Error {
  errors: any[];

  constructor(errors: any[]) {
    super(errors.map((e: any) => e?.message || String(e)).join(', '));
    this.name = 'ProxyGraphQLError';
    this.errors = errors;
  }
}

function isRetryableProxyError(error: unknown): boolean {
  // Retry on common transient failures: throttling, 429/5xx, and network resource issues.
  if (error instanceof ProxyHttpError) {
    return error.status === 429 || (error.status >= 500 && error.status <= 599);
  }

  if (error instanceof ProxyGraphQLError) {
    const msg = error.message || '';
    return /throttled/i.test(msg) || /rate limit/i.test(msg);
  }

  const msg = error instanceof Error ? error.message : String(error);
  return (
    /throttled/i.test(msg)
    || /rate limit/i.test(msg)
    || /failed to fetch/i.test(msg)
    || /insufficient resources/i.test(msg)
    || /networkerror/i.test(msg)
  );
}

function computeBackoffMs(attempt: number): number {
  const base = 500;
  const jitter = Math.floor(Math.random() * 250);
  const exp = Math.min(8000, base * Math.pow(2, attempt));
  return exp + jitter;
}

function isProxyDebugEnabled(): boolean {
  try {
    return Boolean(import.meta.env.DEV && typeof window !== 'undefined' && window.localStorage?.getItem('debugProxy') === '1');
  } catch {
    return false;
  }
}

export function getOauthInstallUrl(shop: string): string {
  return buildOauthInstallUrl(shop);
}

function proxyLog(...args: any[]) {
  if (isProxyDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

function getIdTokenFromUrl(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('id_token');
    if (!token) return null;
    // Basic JWT shape check to avoid passing arbitrary strings as tokens.
    if (token.split('.').length !== 3) return null;
    return token;
  } catch {
    return null;
  }
}

async function getSessionTokenCached(): Promise<string | null> {
  if (!window.shopify) return getIdTokenFromUrl();

  const now = Date.now();

  if (now < idTokenCooldownUntil) {
    return getIdTokenFromUrl();
  }

  if (cachedSessionToken && cachedSessionToken.expiresAt > now) {
    return cachedSessionToken.token;
  }

  if (inFlightSessionToken) {
    return inFlightSessionToken;
  }

  inFlightSessionToken = (async () => {
    try {
      proxyLog('[Shopify Proxy] 🔵 Calling shopify.idToken()...');

      const tokenPromise = window.shopify!.idToken();
      const timeoutPromise = new Promise<string>((_, reject) => {
        setTimeout(() => reject(new Error(`idToken timeout after ${Math.round(ID_TOKEN_TIMEOUT_MS / 1000)}s`)), ID_TOKEN_TIMEOUT_MS);
      });

      const token = await Promise.race([tokenPromise, timeoutPromise]);

      if (token) {
        cachedSessionToken = { token, expiresAt: Date.now() + SESSION_TOKEN_CACHE_MS };
      }

      return token || getIdTokenFromUrl();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.warn('[Shopify Proxy] ⚠️ Could not get ID token:', errorMsg);
      console.warn('[Shopify Proxy] ⚠️ Falling back to legacy auth (cooldown active)');
      idTokenCooldownUntil = Date.now() + ID_TOKEN_FAILURE_COOLDOWN_MS;
      cachedSessionToken = null;
      return getIdTokenFromUrl();
    } finally {
      inFlightSessionToken = null;
    }
  })();

  return inFlightSessionToken;
}

// Supabase configuration for the proxy (required in all environments)
// Do NOT provide a baked-in fallback URL; it will silently point at the wrong project.
const SUPABASE_URL = getRequiredEnv('VITE_SUPABASE_URL');
const SUPABASE_ANON_KEY = getRequiredEnv('VITE_SUPABASE_ANON_KEY');

function buildOauthInstallUrl(shop: string): string {
  const base = SUPABASE_URL.replace(/\/$/, '');
  return `${base}/functions/v1/shopify-auth?${new URLSearchParams({ shop }).toString()}`;
}

/**
 * Initialize the authentication module
 * Called once when the app loads to detect shop domain
 */
export function initializeAuth(): void {
  if (isInitialized) return;
  
  // Strategy 1: Get shop from modern shopify global
  if (window.shopify?.config?.shop) {
    shopDomain = window.shopify.config.shop;
    setSupabaseShopDomain(shopDomain);
    console.log('[Auth] ✅ Got shop from shopify.config.shop:', shopDomain);
    isInitialized = true;
    return;
  }
  
  // Strategy 2: URL params (present on initial OAuth load)
  const params = new URLSearchParams(window.location.search);
  const shopParam = params.get('shop');
  if (shopParam) {
    shopDomain = shopParam;
    setSupabaseShopDomain(shopParam);
    console.log('[Auth] ✅ Got shop from URL param:', shopDomain);

    // Seamless install for public apps:
    // If we're not embedded (no App Bridge global yet), bounce to the Supabase OAuth handler.
    // This handles the initial install flow when Shopify hits the App URL before the app is authorized.
    const shouldAutoRedirect = Boolean((import.meta as any)?.env?.PROD)
      && ((import.meta as any)?.env?.VITE_ENABLE_AUTO_OAUTH_REDIRECT ?? 'true') !== 'false';

    if (shouldAutoRedirect && !window.shopify && window.self === window.top) {
      const installUrl = buildOauthInstallUrl(shopParam);
      console.log('[Auth] Redirecting to OAuth install:', installUrl);
      window.location.assign(installUrl);
      // Note: do not mark initialized; page is navigating.
      return;
    }

    isInitialized = true;
    return;
  }
  
  // Strategy 3: Extract from Shopify Admin URL pattern: /store/SHOP_NAME/apps/...
  const adminUrlMatch = window.location.pathname.match(/\/store\/([^/]+)\//);
  if (adminUrlMatch && adminUrlMatch[1]) {
    shopDomain = `${adminUrlMatch[1]}.myshopify.com`;
    setSupabaseShopDomain(shopDomain);
    console.log('[Auth] ✅ Extracted shop from admin URL:', shopDomain);
    isInitialized = true;
    return;
  }
  
  // Strategy 4: Environment variable (required for production!)
  const envShop = import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN;
  if (envShop) {
    shopDomain = envShop;
    setSupabaseShopDomain(envShop);
    console.log('[Auth] ✅ Using VITE_SHOPIFY_SHOP_DOMAIN:', shopDomain);
    isInitialized = true;
    return;
  }
  
  // All strategies failed - but don't error yet, maybe shopify global will load later
  console.warn('[Auth] ⚠️ Could not determine shop domain at init time');
  console.warn('[Auth] ⚠️ Will retry when making API requests');
}

/**
 * Legacy function for backwards compatibility with App.tsx
 * Now just calls initializeAuth
 */
export function setAppBridge(_app: any): void {
  console.log('[Auth] setAppBridge called - initializing modern auth');
  initializeAuth();
}

/**
 * Check if the modern shopify global is available
 */
export function isShopifyAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.shopify;
}

/**
 * Check if we're running in an embedded Shopify context
 */
export function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check modern shopify global
  if (window.shopify?.environment?.embedded) {
    return true;
  }
  
  // Check URL params (present on initial load)
  const params = new URLSearchParams(window.location.search);
  if (params.has('host') || params.has('shop')) {
    return true;
  }
  
  // Check if running in an iframe (embedded context)
  if (window.self !== window.top) {
    return true;
  }
  
  return false;
}

/**
 * Get the current session token using modern shopify.idToken()
 * This is the ID token that can be exchanged for an access token
 */
export async function getToken(): Promise<string | null> {
  if (!window.shopify) {
    console.warn('[Auth] shopify global not available');
    return null;
  }
  
  try {
    proxyLog('[Auth] Calling shopify.idToken()...');
    const token = await window.shopify.idToken();
    proxyLog('[Auth] ✅ Got ID token');
    return token;
  } catch (error) {
    console.error('[Auth] ❌ shopify.idToken() failed:', error);
    return null;
  }
}

/**
 * Get a cached Shopify session token for backend calls.
 * Prefer this over calling shopify.idToken() directly to avoid bursts.
 */
export async function getSessionTokenForBackend(): Promise<string | null> {
  const urlToken = getIdTokenFromUrl();

  // Prefer a fresh cached token when embedded, but fall back to the URL token
  // for first-load flows where App Bridge isn't ready yet.
  if (window.shopify && isEmbedded()) {
    return (await getSessionTokenCached()) || urlToken;
  }

  return urlToken;
}

type ShopifyAuthProbeResult =
  | { status: 'ok' }
  | { status: 'install_required'; shopDomain: string; installUrl: string }
  | { status: 'error'; error: string };

/**
 * Minimal probe to detect missing install/auth early and show a friendly UI.
 */
export async function probeShopifyAuth(): Promise<ShopifyAuthProbeResult> {
  const shop = getShopDomain();
  if (!shop) {
    return { status: 'error', error: 'Missing shop domain' };
  }

  const query = `query AuthProbe { shop { id name } }`;

  try {
    await shopifyAdminGraphQL(query);
    return { status: 'ok' };
  } catch (error) {
    const status = (error as any)?.status;
    const bodyText = (error as any)?.bodyText;
    const message = error instanceof Error ? error.message : String(error);

    if (status === 401) {
      return {
        status: 'install_required',
        shopDomain: shop,
        installUrl: buildOauthInstallUrl(shop),
      };
    }

    // Surface a compact message; bodyText can be huge/JSON.
    return {
      status: 'error',
      error: typeof bodyText === 'string' && bodyText.trim() ? bodyText : message,
    };
  }
}

/**
 * Legacy getter for backwards compatibility
 */
export function getAppBridge(): any {
  console.warn('[Auth] getAppBridge() is deprecated - use window.shopify instead');
  return null;
}

/**
 * Get the shop domain from various sources
 */
export function getShopDomain(): string | null {
  // Check stored domain
  if (shopDomain) return shopDomain;
  
  // Try to initialize if not done yet
  initializeAuth();
  if (shopDomain) return shopDomain;
  
  // Check modern shopify global (may have loaded since init)
  if (window.shopify?.config?.shop) {
    shopDomain = window.shopify.config.shop;
    setSupabaseShopDomain(shopDomain);
    return shopDomain;
  }
  
  // Check URL params
  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop');
  if (shop) {
    shopDomain = shop;
    setSupabaseShopDomain(shop);
    return shop;
  }
  
  // Extract from Shopify Admin URL pattern
  const adminUrlMatch = window.location.pathname.match(/\/store\/([^/]+)\//);
  if (adminUrlMatch && adminUrlMatch[1]) {
    shopDomain = `${adminUrlMatch[1]}.myshopify.com`;
    setSupabaseShopDomain(shopDomain);
    return shopDomain;
  }
  
  // Fallback to env variable
  return import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || null;
}

/**
 * Make a GraphQL request to Shopify Admin API via Supabase proxy
 * 
 * Authentication flow:
 * 1. Gets session token via shopify.idToken() (modern App Bridge)
 * 2. Sends token to Supabase proxy
 * 3. Proxy exchanges session token + client credentials for access token (Token Exchange)
 * 4. If no session token available, proxy falls back to legacy shpat_ token
 */
export async function shopifyAdminGraphQL<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: { preferOfflineToken?: boolean }
): Promise<T> {
  // Get current shop domain for the request
  const currentShop = getShopDomain();
  
  if (!currentShop) {
    console.error('[Auth] ❌ No shop domain available - request will likely fail');
  }
  
  proxyLog('[Shopify Proxy] ✅ Making request via Supabase proxy for shop:', currentShop);

  // Call the Supabase Edge Function proxy
  const proxyUrl = `${SUPABASE_URL}/functions/v1/shopify-proxy`;
  
  proxyLog('[Shopify Proxy] URL:', proxyUrl);
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  };

  if (options?.preferOfflineToken) {
    // Instruct the proxy to use the shop's offline (DB) token when available.
    // This avoids user-bound permission limitations on token-exchange (online) tokens.
    headers['x-shopify-prefer-offline-token'] = '1';
  }
  
  // Pass shop domain to proxy for multi-tenant support
  if (currentShop) {
    headers['x-shop-domain'] = currentShop;
  }
  
  // Get session token using modern shopify.idToken() (cached + single-flight)
  if (window.shopify && isEmbedded()) {
    const sessionToken = await getSessionTokenCached();
    if (sessionToken) {
      headers['x-shopify-session-token'] = sessionToken;
      proxyLog('[Shopify Proxy] ✅ Session token attached');
    }
  } else {
    proxyLog('[Shopify Proxy] ℹ️ Embedded session token not available - using legacy auth');
  }

  // Add debug ID for request tracing
  const debugId = `ui-debug-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  headers['x-debug-id'] = debugId;

  const runOnce = async (): Promise<T> => {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('[Shopify Proxy] ❌ Request timeout after 30 seconds');
      controller.abort();
    }, 30000);

    try {
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query,
          variables,
          shop_domain: currentShop,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[Shopify Proxy] ❌ HTTP error:', response.status, text);
        throw new ProxyHttpError(response.status, text);
      }

      const result = await response.json();

      if (result.errors?.length > 0) {
        console.error('[Shopify Proxy] ❌ GraphQL errors:', result.errors);
        throw new ProxyGraphQLError(result.errors);
      }

      if (!result.data) {
        console.error('[Shopify Proxy] ❌ No data in response:', result);
        throw new Error('No data returned from Shopify API');
      }

      proxyLog('[Shopify Proxy] ✅ Request successful');
      return result.data;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  await acquireProxySlot();
  try {
    for (let attempt = 0; attempt <= PROXY_MAX_RETRIES; attempt += 1) {
      try {
        return await runOnce();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const isAbort = error instanceof Error && error.name === 'AbortError';
        const retryable = !isAbort && isRetryableProxyError(error);
        const isLast = attempt >= PROXY_MAX_RETRIES;

        console.error('[Shopify Proxy] ❌ Exception:', message);

        if (!retryable || isLast) {
          if (isAbort) {
            throw new Error('Shopify API proxy request timeout');
          }
          throw error;
        }

        const backoff = computeBackoffMs(attempt);
        console.warn(`[Shopify Proxy] ⚠️ Retry ${attempt + 1}/${PROXY_MAX_RETRIES} in ${backoff}ms`);
        await sleep(backoff);
      }
    }

    // Unreachable, but TypeScript wants a return.
    throw new Error('Shopify proxy retry loop exhausted');
  } finally {
    releaseProxySlot();
  }
}
